import React, { useState } from "react";
import styled from "styled-components";
import { Modal, Select, Radio, Input, Upload, Form, App, Button } from "antd";
import { CloseOutlined, UploadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import theme from "../../themes";
import { useAppDispatch, useAppSelector } from "../../store";
import { createLineThunk } from "../../store/actions/line.action";
import ToastMessage from "../../utils/toastMessage";
import { ReactComponent as CheckCircleIcon } from "../../assets/icons/checkCircle.svg";
import * as yup from "yup";
import { ErrorCircle } from "../../components/icons/errorCircle";
import {
  formLabelColWide,
  formOffsetColWide,
  formWrapperColWide,
} from "../../styles/responsive";
import { useNavigate } from "react-router-dom";
import { useChannelSettings } from "../../hooks/useChannelSettings";

const { Text, Link } = require("antd").Typography;

// Define the form data interface for Ant Design Form
interface PhoneNumberFormData {
  phone_type: 1 | 2; // 1 for LOCAL, 2 for TOLL_FREE
  phone_service: 1 | 2; // 1 for CALLS_ONLY, 2 for CALLS_SMS
  company_number: string;
  address: string;
  name: string;
  country_code: string;
  customer_proof_type: 1 | 2; // 1 for WEBSITE_URL, 2 for UPLOAD_IMG
  ein: string;
  customer_proof_website: string;
  file?: File | undefined;
}

// Yup validation schema - will be created inside component to access translations

interface CreateLineModalProps {
  open: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const CreateLineModal: React.FC<CreateLineModalProps> = ({
  open,
  onCancel,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { message } = App.useApp();
  const toast = ToastMessage(message);
  const navigate = useNavigate();
  const { loading } = useAppSelector((state) => state.line);
  const { isEnabled } = useChannelSettings();

  // Yup validation schema with translations
  const lineSchema = yup.object().shape({
    phone_type: yup
      .number()
      .oneOf([1, 2])
      .required(t("settings.createLineModal.validation.phoneTypeRequired")),
    phone_service: yup
      .number()
      .oneOf([1, 2])
      .required(t("settings.createLineModal.validation.serviceTypeRequired")),
    company_number: yup
      .string()
      .required(
        t("settings.createLineModal.validation.companyPhoneNumberRequired")
      )
      .matches(
        /^[+]?[1-9][\d]{0,15}$/,
        t("settings.createLineModal.validation.companyPhoneNumberInvalid")
      ),
    address: yup
      .string()
      .required(
        t("settings.createLineModal.validation.companyAddressRequired")
      ),
    name: yup
      .string()
      .required(t("settings.createLineModal.validation.companyNameRequired")),
    country_code: yup
      .string()
      .required(t("settings.createLineModal.validation.countryRequired")),
    customer_proof_type: yup
      .number()
      .oneOf([1, 2])
      .when("phone_service", {
        is: 2,
        then: (schema) =>
          schema.required(
            t("settings.createLineModal.validation.proofMethodRequired")
          ),
        otherwise: (schema) => schema.optional(),
      }),
    ein: yup
      .string()
      .required(t("settings.createLineModal.validation.companyEinRequired"))
      .matches(
        /^\d{2}-\d{7}$/,
        t("settings.createLineModal.validation.companyEinFormat")
      ),
    customer_proof_website: yup
      .string()
      .when(["customer_proof_type", "phone_service"], {
        is: (customer_proof_type: number, phone_service: number) =>
          phone_service === 2 && customer_proof_type === 1,
        then: (schema) =>
          schema
            .required(
              t("settings.createLineModal.validation.websiteUrlRequired")
            )
            .url(t("settings.createLineModal.validation.websiteUrlInvalid")),
        otherwise: (schema) => schema.optional(),
      }),
    file: yup.mixed().when(["customer_proof_type", "phone_service"], {
      is: (customer_proof_type: number, phone_service: number) =>
        phone_service === 2 && customer_proof_type === 2,
      then: (schema) =>
        schema.required(
          t("settings.createLineModal.validation.screenshotRequired")
        ),
      otherwise: (schema) => schema.optional(),
    }),
  });

  // Default form values
  const defaultFormValues: PhoneNumberFormData = {
    phone_type: 1, // LOCAL
    phone_service: 1, // CALLS_ONLY
    company_number: "",
    address: "",
    name: "",
    country_code: "US", // Default to United States
    customer_proof_type: 1, // WEBSITE_URL
    ein: "",
    customer_proof_website: "",
    file: undefined,
  };

  const [form] = Form.useForm<PhoneNumberFormData>();
  const [proofMethod, setProofMethod] = useState<1 | 2>(1);
  const [uploadedFile, setUploadedFile] = useState<File | undefined>(undefined);
  const [fileValidationError, setFileValidationError] = useState<string | null>(
    null
  );
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [createdPhoneNumber, setCreatedPhoneNumber] = useState<string>("");
  const service = (Form.useWatch("phone_service", form) ??
    defaultFormValues.phone_service) as 1 | 2;

  const validateFile = (file: File): string | null => {
    // Check file type
    const allowedTypes = [".jpg", ".jpeg", ".png"];
    const fileExtension = "." + file.name.split(".").pop()?.toLowerCase();

    if (!allowedTypes.includes(fileExtension)) {
      return t("settings.createLineModal.validation.fileTypeNotSupported");
    }

    // Check file size (10MB = 10 * 1024 * 1024 bytes)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      return t("settings.createLineModal.validation.fileExceedsMaxSize");
    }

    return null;
  };

  const resetForm = () => {
    form.resetFields();
    setProofMethod(1);
    setUploadedFile(undefined);
    setFileValidationError(null);
    setCreatedPhoneNumber("");
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  const onSubmit = async (values: PhoneNumberFormData) => {
    try {
      // Validate with Yup first
      await lineSchema.validate(values, { abortEarly: false });

      const result = await dispatch(createLineThunk(values)).unwrap();
      onSuccess();

      // Store the created phone number for the success modal
      setCreatedPhoneNumber(
        result.data?.line_number || "Your new phone number"
      );

      // Close the create modal and show success modal
      onCancel();
      setIsSuccessModalOpen(true);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        // Handle Yup validation errors
        const fieldErrors: Record<string, string[]> = {};
        error.inner.forEach((err) => {
          if (err.path) {
            fieldErrors[err.path] = [err.message];
          }
        });
        form.setFields(
          Object.entries(fieldErrors).map(([name, errors]) => ({
            name: name as keyof PhoneNumberFormData,
            errors,
          }))
        );
      } else {
        toast.error(
          t("settings.createLineModal.validation.failedToCreatePhoneNumber"),
          5
        );
      }
    }
  };

  const handleSuccessModalClose = () => {
    handleCancel();
    setIsSuccessModalOpen(false);
  };

  const handleValuesChange = (changedValues: any) => {
    const changedFieldName = Object.keys(
      changedValues
    )[0] as keyof PhoneNumberFormData;
    if (!changedFieldName) return;

    if (changedFieldName === "customer_proof_type") {
      const value = changedValues.customer_proof_type as 1 | 2;
      setProofMethod(value);
      // Clear errors of mutually exclusive proof fields
      form.setFields([
        { name: "customer_proof_website", errors: [] },
        { name: "file", errors: [] },
      ]);
    }

    // Validate only the changed field against Yup schema using current values
    const allValues = {
      ...form.getFieldsValue(true),
      ...changedValues,
    } as PhoneNumberFormData;
    (async () => {
      try {
        await lineSchema.validateAt(changedFieldName as string, allValues, {
          abortEarly: true,
        });
        // Field is valid -> clear its errors
        form.setFields([{ name: changedFieldName, errors: [] }]);
      } catch (e) {
        if (e instanceof yup.ValidationError) {
          // Field invalid -> show its first error message
          form.setFields([{ name: changedFieldName, errors: [e.message] }]);
        }
      }
    })();
  };

  const handleNavigateToManagePhoneNumbers = () => {
    navigate("/settings");
  };

  return (
    <>
      <Modal
        title={
          <div style={{ position: "relative", margin: "0 -24px 0 -24px" }}>
            <div
              style={{
                fontSize: 18,
                fontWeight: 500,
                padding: "4px 0 24px 24px",
              }}
            >
              {t("settings.createLineModal.title")}
            </div>
            <div
              style={{
                borderBottom: `1px solid ${theme.colors.grey2}`,
                width: "100%",
                marginBottom: 24,
              }}
            />
          </div>
        }
        closeIcon={
          <CloseOutlined
            style={{ fontSize: 11, fontWeight: 700, color: "#000" }}
          />
        }
        modalRender={(modal) => (
          <StyledModalWrapper>{modal}</StyledModalWrapper>
        )}
        open={open}
        onCancel={handleCancel}
        footer={null}
        width="min(900px, calc(100vw - 24px))"
      >
        <Form
          requiredMark={false}
          labelCol={formLabelColWide}
          wrapperCol={formWrapperColWide}
          labelAlign="left"
          labelWrap
          colon={false}
          form={form}
          onFinish={onSubmit}
          initialValues={defaultFormValues}
          onValuesChange={(changedValues) => handleValuesChange(changedValues)}
        >
          <div style={{ marginBottom: 24 }}>
            <Text>{t("settings.createLineModal.description")}</Text>
          </div>

          <Form.Item
            label={t("settings.createLineModal.typeOfPhoneNumber")}
            name="phone_type"
          >
            <Select
              placeholder={t("settings.createLineModal.selectNumberType")}
              size="large"
              style={{ width: 300 }}
              options={[
                { label: t("settings.createLineModal.localNumber"), value: 1 },
                {
                  label: t("settings.createLineModal.tollFreeNumber"),
                  value: 2,
                },
              ]}
            />
          </Form.Item>

          <Form.Item
            label={t("settings.createLineModal.service")}
            name="phone_service"
          >
            <Radio.Group
              options={[
                { value: 1, label: t("settings.createLineModal.callsOnly") },
                ...(isEnabled("sms")
                  ? [
                      {
                        value: 2,
                        label: t("settings.createLineModal.callsAndTextMessages"),
                      },
                    ]
                  : []),
              ]}
            />
          </Form.Item>

          <>
            <Form.Item
              label={t("settings.createLineModal.companyName")}
              name="name"
            >
              <Input
                size="large"
                placeholder={t("settings.createLineModal.typeCompanyName")}
                style={{ width: 380 }}
              />
            </Form.Item>

            <Form.Item
              label={t("settings.createLineModal.country")}
              name="country_code"
            >
              <Select
                placeholder={t("settings.createLineModal.selectCountry")}
                size="large"
                style={{ width: 380 }}
                defaultValue="US"
                options={[
                  {
                    label: t("settings.createLineModal.unitedStates"),
                    value: "US",
                  },
                  {
                    label: t("settings.createLineModal.canada"),
                    value: "CA",
                  },
                  {
                    label: t("settings.createLineModal.england"),
                    value: "GB",
                  },
                ]}
              />
            </Form.Item>

            <Form.Item
              label={t("settings.createLineModal.companyPhoneNumber")}
              name="company_number"
            >
              <Input
                size="large"
                placeholder={t(
                  "settings.createLineModal.typeCompanyPhoneNumber"
                )}
                style={{ width: 380 }}
              />
            </Form.Item>

            <Form.Item
              label={t("settings.createLineModal.companyAddress")}
              name="address"
            >
              <Input
                size="large"
                placeholder={t("settings.createLineModal.typeCompanyAddress")}
              />
            </Form.Item>

            <Form.Item
              label={t("settings.createLineModal.companyEin")}
              name="ein"
            >
              <Input
                size="large"
                placeholder={t("settings.createLineModal.typeCompanyEin")}
                style={{ width: 380 }}
              />
            </Form.Item>

            {service === 2 && (
              <>
                <Form.Item
                  label={t("settings.createLineModal.proofOfCustomerOptIn")}
                  name="customer_proof_type"
                >
                  <Radio.Group onChange={(e) => setProofMethod(e.target.value)}>
                    <Radio value={1}>
                      {t("settings.createLineModal.provideWebsiteUrl")}
                    </Radio>
                    <Radio value={2}>
                      {t("settings.createLineModal.uploadScreenshot")}
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                {proofMethod === 1 ? (
                  <Form.Item
                    name="customer_proof_website"
                    wrapperCol={formOffsetColWide}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 16,
                        width: "100%",
                      }}
                    >
                      <div style={{ fontWeight: 600, fontSize: 14 }}>
                        {t("settings.createLineModal.websiteUrl")}
                      </div>
                      <Input
                        size="large"
                        placeholder={t(
                          "settings.createLineModal.typeWebsiteUrl"
                        )}
                        style={{ flex: 1 }}
                      />
                    </div>
                  </Form.Item>
                ) : (
                  <Form.Item name="file" wrapperCol={formOffsetColWide}>
                    {uploadedFile && (
                      <FileUploadedContainer>
                        <CheckCircleIcon
                          style={{ color: theme.colors.green1, marginRight: 8 }}
                        />
                        <FileUploadedText>
                          {t("settings.createLineModal.fileUploaded")}:{" "}
                          <FileName>{uploadedFile.name}</FileName>
                        </FileUploadedText>
                      </FileUploadedContainer>
                    )}
                    {fileValidationError && (
                      <FileErrorContainer>
                        <ErrorCircle />
                        <FileErrorText>{fileValidationError}</FileErrorText>
                      </FileErrorContainer>
                    )}
                    <Upload
                      name="file"
                      beforeUpload={() => false}
                      onChange={(info) => {
                        const f = info.fileList[0]?.originFileObj as
                          | File
                          | undefined;

                        if (!f) {
                          setFileValidationError(null);
                          setUploadedFile(undefined);
                          form.setFieldValue("file", undefined);
                          return;
                        }
                        const error = validateFile(f);
                        if (error) {
                          setFileValidationError(error);
                          setUploadedFile(undefined);
                          form.setFieldValue("file", undefined);
                        } else {
                          setFileValidationError(null);
                          setUploadedFile(f);
                          form.setFieldValue("file", f);
                        }
                      }}
                      accept=".jpg,.jpeg,.png"
                      maxCount={1}
                      showUploadList={false}
                    >
                      <Button size="large">
                        <UploadOutlined />
                        <span style={{ marginLeft: 8, fontSize: 16 }}>
                          {uploadedFile
                            ? t(
                                "settings.createLineModal.uploadAnotherScreenshot"
                              )
                            : t(
                                "settings.createLineModal.uploadScreenshotButton"
                              )}
                        </span>
                      </Button>
                    </Upload>
                    <HintText>
                      {t("settings.createLineModal.maxFileSize")}
                    </HintText>
                  </Form.Item>
                )}

                <Form.Item wrapperCol={formOffsetColWide}>
                  <div
                    style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}
                  >
                    {t("settings.createLineModal.whatIsProofOfCustomerOptIn")}
                  </div>
                  <div
                    style={{
                      color: theme.colors.grey1,
                      fontSize: 13,
                      lineHeight: 1.6,
                    }}
                  >
                    {t(
                      "settings.createLineModal.proofOfCustomerOptInDescription"
                    )}
                  </div>
                </Form.Item>
              </>
            )}
          </>

          <ModalFooter>
            <FooterButton type="button" onClick={handleCancel}>
              {t("settings.createLineModal.cancel")}
            </FooterButton>
            <FooterButton type="submit" primary disabled={loading}>
              {loading ? (
                <>
                  <LoadingSpinner />
                  {t("settings.createLineModal.creating")}
                </>
              ) : (
                t("settings.createLineModal.create")
              )}
            </FooterButton>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Success Modal */}
      <Modal
        open={isSuccessModalOpen}
        onCancel={handleSuccessModalClose}
        footer={[
          <GotItButton onClick={handleSuccessModalClose}>
            {t("settings.createLineModal.gotIt")}
          </GotItButton>,
        ]}
        width="min(600px, calc(100vw - 24px))"
        closable={true}
        closeIcon={<CloseOutlined style={{ fontSize: 16, fontWeight: 700 }} />}
      >
        <SuccessModalContent>
          <SuccessHeader>
            <CheckCircleIcon
              style={{ color: theme.colors.green1, marginRight: 12 }}
            />
            <SuccessTitle>
              {t("settings.createLineModal.phoneNumberCreated")}
            </SuccessTitle>
          </SuccessHeader>

          <SuccessBody>
            <SuccessText>
              {t("settings.createLineModal.yourNewPhoneNumberIs")}{" "}
              <PhoneNumberHighlight>{createdPhoneNumber}</PhoneNumberHighlight>.
            </SuccessText>

            {service === 1 ? (
              <SuccessText>
                {t("settings.createLineModal.youCanCheckStatus")}{" "}
                <ManageLink onClick={handleNavigateToManagePhoneNumbers}>
                  {t("settings.createLineModal.managePhoneNumbersButton")}
                </ManageLink>{" "}
                {t("settings.createLineModal.buttonUnderSettingsPage")}
              </SuccessText>
            ) : (
              <>
                <SuccessText>
                  {t("settings.createLineModal.youCanUseCallingService")}
                </SuccessText>
                <SuccessText>
                  {t("settings.createLineModal.messagingServiceMayTakeDays")}{" "}
                  {t("settings.createLineModal.youCanCheckStatus")}{" "}
                  <ManageLink onClick={handleNavigateToManagePhoneNumbers}>
                    {t("settings.createLineModal.managePhoneNumbersButton")}
                  </ManageLink>{" "}
                  {t("settings.createLineModal.buttonUnderSettingsPage")}
                </SuccessText>
              </>
            )}
          </SuccessBody>
        </SuccessModalContent>
      </Modal>
    </>
  );
};

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  flex-wrap: wrap;

  @media (max-width: 480px) {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const FooterButton = styled.button<{ primary?: boolean; disabled?: boolean }>`
  background: ${({ primary, disabled }) =>
    disabled
      ? theme.colors.grey2
      : primary
      ? theme.colors.green1
      : theme.colors.grey3};
  color: ${({ primary, disabled }) =>
    disabled ? theme.colors.grey1 : primary ? "#fff" : "#000"};
  border: 1px solid
    ${({ primary, disabled }) =>
      disabled
        ? theme.colors.grey2
        : primary
        ? theme.colors.green1
        : theme.colors.grey2};
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition: all 0.2s;

  &:hover {
    background: ${({ primary, disabled }) =>
      disabled
        ? theme.colors.grey2
        : primary
        ? theme.colors.green1
        : theme.colors.grey3};
  }

  &:disabled {
    opacity: 0.5;
  }
`;

const HintText = styled.span`
  color: ${theme.colors.grey1};
  font-size: 14px;
  margin-left: 24px;
`;

const StyledModalWrapper = styled.div`
  .ant-modal-close {
    right: 24px;
    top: 20px;
  }

  .ant-form-item {
    margin-bottom: 16px;
  }

  .ant-form-item-label > label {
    font-weight: 600;
    font-size: 16px;
    white-space: normal;
  }

  /* Ensure no colon after labels regardless of antd defaults */
  .ant-form-item-label > label::after {
    content: "" !important;
  }
`;

const FileUploadedContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  border-radius: 8px;
`;

const FileUploadedText = styled.span`
  font-size: 16px;
  font-weight: 400;
  color: ${theme.colors.grey1};
`;

const FileName = styled.span`
  font-weight: 500;
  color: #000;
`;

const FileErrorContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  border-radius: 8px;
`;

const FileErrorText = styled.span`
  font-size: 16px;
  font-weight: 400;
  margin-left: 8px;
`;

const SuccessModalContent = styled.div``;

const SuccessHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 18px;
`;

const SuccessTitle = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: #000;
`;

const SuccessBody = styled.div`
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SuccessText = styled.p`
  font-size: 16px;
  font-weight: 400;

  &:last-child {
    margin-bottom: 0;
  }
`;

const PhoneNumberHighlight = styled.span`
  font-weight: 600;
  color: #000;
`;

const ManageLink = styled.span`
  color: #3b82f6;
  cursor: pointer;
`;

const GotItButton = styled(Button)`
  background: ${theme.colors.green1};
  color: #fff;
  border: none;
  border-radius: 8px;
  height: 40px;
  padding: 0 16px;
  font-size: 16px;
  font-weight: 400;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #047857 !important;
    color: #fff !important;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

export default CreateLineModal;
